import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, ArrowRight, Shield, Lock } from '@/components/DeskeyIcons';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const G = '#34A853';

// The MyDeskey portal is for institution admins only. Teachers and students
// sign in on their institution's own login page (/<endpoint>).
const PORTAL_ROLE = 'institution';

const Logo = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.5 7.2C11.5 4.1 15 2.2 17.6 3.9L34.2 15.7C36.5 17.3 36.5 20.7 34.2 22.3L17.6 34.1C15 35.8 11.5 33.9 11.5 30.8V7.2Z" fill="#34A853" />
  </svg>
);

export default function MyDeskeyPage() {
  const nav = useNavigate();
  const [mode, setMode] = useState('login'); // login | 2fa | forgot | reset-confirm (signup removed — accounts are created by admins)
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', fullName: '', phone: '', institutionName: '', institutionType: 'school' });
  const [twoFACode, setTwoFACode] = useState('');
  const [devCode, setDevCode] = useState('');
  const [twoFAMethod, setTwoFAMethod] = useState('email');  // 'email' | 'totp'
  const [preToken, setPreToken] = useState(''); // For TOTP-gated logins
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [resetDevCode, setResetDevCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/mydeskey/login`,
        { email: form.email, password: form.password, expected_role: PORTAL_ROLE });

      // NEW: TOTP-gated accounts hand back { needs_2fa, method:'totp', pre_token, user }
      // — no real bearer JWT until /api/auth/2fa/verify-totp succeeds.
      if (data.needs_2fa && data.method === 'totp') {
        setPreToken(data.pre_token);
        setTwoFAMethod('totp');
        setUseBackupCode(false);
        setTwoFACode('');
        setDevCode('');
        setMode('2fa');
        sessionStorage.setItem('pending_user', JSON.stringify(data.user || null));
      } else if (data.needs2FA) {
        // Legacy email-OTP flow for institution admins not yet on TOTP.
        const fa = await axios.post(`${API_URL}/api/auth/2fa/send`,
          { email: form.email },
          { headers: { Authorization: `Bearer ${data.token}` } });
        setDevCode(fa.data?.dev_code || '');
        setTwoFAMethod('email');
        sessionStorage.setItem('pending_token', data.token);
        sessionStorage.setItem('pending_user', JSON.stringify(data.user));
        sessionStorage.setItem('pending_tenant', JSON.stringify(data.tenant || null));
        setMode('2fa');
      } else {
        completeLogin(data.token, data.user, data.tenant);
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Teacher/student account on the admin-only portal → explain where to sign in.
      if (status === 403 && detail && typeof detail === 'object' && ['wrong_page', 'wrong_tab'].includes(detail.code)) {
        setError({ wrongPage: true, message: detail.message });
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.message || err.message || 'Login failed');
        setError(msg);
      }
    }
    setLoading(false);
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      if (twoFAMethod === 'totp') {
        // TOTP step-2: exchange pre_token + code for a real bearer JWT.
        const url = useBackupCode
          ? `${API_URL}/api/auth/2fa/verify-backup-code`
          : `${API_URL}/api/auth/2fa/verify-totp`;
        const body = useBackupCode
          ? { pre_token: preToken, backup_code: twoFACode.toUpperCase().trim() }
          : { pre_token: preToken, code: twoFACode };
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Invalid code');
        const u = data.user || JSON.parse(sessionStorage.getItem('pending_user') || 'null');
        completeLogin(data.access_token, u, null);
        return;
      }
      // Legacy email-OTP path.
      const res = await fetch(`${API_URL}/api/auth/2fa/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, code: twoFACode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invalid code');
      const tk = sessionStorage.getItem('pending_token');
      const u = JSON.parse(sessionStorage.getItem('pending_user'));
      const t = JSON.parse(sessionStorage.getItem('pending_tenant') || 'null');
      completeLogin(tk, u, t);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/password-reset/request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Request failed');
      setResetDevCode(data.dev_code || '');
      setSuccess('Reset code sent to your email.');
      setMode('reset-confirm');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const handleResetConfirm = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/password-reset/confirm`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, code: resetCode, new_password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reset failed');
      setSuccess(data.message);
      setMode('login');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const completeLogin = (token, user, tenant) => {
    localStorage.setItem('desky_token', token);
    localStorage.setItem('desky_user', JSON.stringify(user));
    if (tenant) localStorage.setItem('desky_tenant', JSON.stringify(tenant));
    sessionStorage.removeItem('pending_token');
    sessionStorage.removeItem('pending_user');
    sessionStorage.removeItem('pending_tenant');
    // Redirect based on role
    const role = user.role;
    if (role === 'tenant_owner' || role === 'tenant_admin') window.location.replace('/institution');
    else if (role === 'department_admin' || role === 'department_head') window.location.replace('/department');
    else if (role === 'teacher') window.location.replace(user.licence_status === 'active' ? '/teacher' : '/activate-licence');
    else if (role === 'student') {
      // New MyDeskey student flow — onboarding if profile not complete.
      // Check BOTH possible locations because legacy users have the flag
      // at the top level while newer ones have it nested in student_profile.
      const onboarded = user?.student_profile?.onboarding_completed
        || user?.onboarding_completed;
      window.location.replace(onboarded ? '/student/desk' : '/student/onboarding');
    }
    else window.location.replace('/');
  };


  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="mydeskey-page">
      <header className="flex items-center justify-between px-6 sm:px-10 py-4 border-b border-gray-100">
        <button onClick={() => nav('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="mydeskey-logo">
          <Logo size={28} />
          <span className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Deskey</span>
        </button>
      </header>

      <main className="flex-1 flex justify-center items-start lg:items-center px-0 lg:h-[calc(100vh-64px)] lg:overflow-hidden">
        <div className="w-full h-full grid lg:grid-cols-2 items-start lg:items-center">
          {/* Left branding */}
          <div className="hidden lg:flex flex-col justify-center relative overflow-hidden bg-white">
            <div className="absolute bottom-0 left-0 w-full h-[40%]" style={{ background: `linear-gradient(180deg, transparent 0%, ${G}04 100%)` }} />
            <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full" style={{ background: `${G}06` }} />
            <div className="relative z-10 px-14 xl:px-20">
              <h1 className="text-[3.5rem] xl:text-[4.2rem] font-bold text-gray-900 leading-[1.08] tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                My<span style={{ color: G }}>Deskey</span>
              </h1>
              <p className="mt-4 text-lg text-gray-400 leading-relaxed max-w-md">
                One dashboard to power every classroom. Sign in to manage AI-generated lessons, track student progress, and grow your institution.
              </p>
              <div className="mt-8 space-y-3.5">
                {[
                  { num: '10M+', text: 'AI lessons delivered across 25+ languages' },
                  { num: '500+', text: 'Institutions from schools to universities' },
                  { num: '24/7', text: 'Personalized AI tutor for every student' },
                ].map((item, i) => (
                  <div key={i} className="flex items-baseline gap-3">
                    <span className="text-xl font-bold tracking-tight" style={{ color: G, fontFamily: 'Space Grotesk, sans-serif' }}>{item.num}</span>
                    <span className="text-sm text-gray-400">{item.text}</span>
                  </div>
                ))}
              </div>
              <div className="mt-10 pt-8 border-t border-gray-100">
                <p className="text-[11px] text-gray-400 tracking-widest uppercase mb-4">Trusted by leading institutions</p>
                <div className="relative overflow-hidden h-6">
                  <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10" />
                  <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-10" />
                  <div className="flex items-center gap-8 animate-trust-scroll whitespace-nowrap">
                    {['Delhi Public School','IIT Madras','NIT Trichy','VIT University','BITS Pilani','Narayana Group','Amity University','Manipal Academy','Christ University','Symbiosis International',
                      'Delhi Public School','IIT Madras','NIT Trichy','VIT University','BITS Pilani','Narayana Group','Amity University','Manipal Academy','Christ University','Symbiosis International'
                    ].map((n, i) => <span key={i} className="text-xs font-medium text-gray-500 flex-shrink-0">{n}</span>)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile branding */}
          <div className="lg:hidden text-center pt-8 pb-2 px-4">
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>My<span style={{ color: G }}>Deskey</span></h1>
            <p className="text-sm text-gray-400 mt-1">Institution admin sign in</p>
          </div>

          {/* Right — Form */}
          <div className="flex justify-center items-start lg:items-center px-4 sm:px-8 lg:px-12 py-8 lg:py-0 bg-gray-50/50 lg:border-l lg:border-gray-100 w-full min-h-[60vh] lg:h-full">
            <div className="w-full max-w-sm lg:max-w-md lg:min-h-[520px] flex flex-col" data-testid="mydeskey-login-card">
              <div className="mb-5">
                <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Institution sign in</h2>
              </div>

              {/* LOGIN FORM */}
              {mode === 'login' && (
                <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Email</label>
                    <Input data-testid="login-email" type="email" placeholder="Enter your institution email" value={form.email} onChange={e => f('email', e.target.value)} required className="h-11 rounded-xl border-gray-200 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Password</label>
                    <div className="relative">
                      <Input data-testid="login-password" type={show ? 'text' : 'password'} placeholder="Enter your password" value={form.password} onChange={e => f('password', e.target.value)} required className="h-11 rounded-xl border-gray-200 text-sm pr-10" />
                      <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  {error && (typeof error === 'object' && error.wrongPage ? (
                    <div data-testid="login-error" role="alert"
                      className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3 flex items-start gap-2.5">
                      <Shield className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-amber-900">This account can&apos;t sign in here</p>
                        <p className="text-xs text-amber-800 mt-0.5">{error.message}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-red-500" data-testid="login-error">{typeof error === 'string' ? error : (error.message || 'Login failed')}</p>
                  ))}
                  <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full h-11 rounded-xl font-semibold text-white text-sm" style={{ background: G }}>
                    {loading ? 'Signing in...' : 'Sign In'} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                  </Button>
                  <div className="flex items-center gap-1.5 justify-center text-[10px] text-gray-400"><Shield className="w-3 h-3" /> 2-Factor Authentication enabled</div>
                  <div className="flex justify-between pt-1">
                    <button type="button" data-testid="forgot-password-btn" onClick={() => { setMode('forgot'); setError(''); }} className="text-xs font-medium hover:underline" style={{ color: G }}>Forgot Password?</button>
                    <button type="button" onClick={() => nav('/')} className="text-xs text-gray-400 hover:text-gray-600">Back to Home</button>
                  </div>
                </form>
              )}

              {/* 2FA */}
              {mode === '2fa' && (
                <form onSubmit={handleVerify2FA} className="space-y-4" data-testid="2fa-form">
                  <div className="text-center mb-2">
                    <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3"><Lock className="w-6 h-6" style={{ color: G }} /></div>
                    <h3 className="text-base font-bold text-gray-900">
                      {twoFAMethod === 'totp'
                        ? (useBackupCode ? 'Use a Recovery Code' : 'Open Your Authenticator')
                        : 'Verify Your Identity'}
                    </h3>
                    <p className="text-xs text-gray-400 mt-1">
                      {twoFAMethod === 'totp'
                        ? (useBackupCode
                            ? 'Enter one of your 8 one-time backup codes (XXXX-XXXX)'
                            : 'Type the 6-digit code from Google Authenticator / Microsoft Authenticator')
                        : 'Enter the 6-digit code sent to your email'}
                    </p>
                    {devCode && twoFAMethod === 'email' && <p className="text-[10px] text-orange-500 mt-1">(Dev mode: {devCode})</p>}
                  </div>
                  <Input
                    data-testid="2fa-code-input"
                    placeholder={useBackupCode ? 'XXXX-XXXX' : 'Enter 6-digit code'}
                    value={twoFACode}
                    onChange={e => {
                      const v = e.target.value;
                      if (useBackupCode) {
                        // Allow A–Z, 0–9, and hyphen; pad with hyphen at position 4
                        setTwoFACode(v.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 9));
                      } else {
                        setTwoFACode(v.replace(/\D/g, '').slice(0, 6));
                      }
                    }}
                    required
                    maxLength={useBackupCode ? 9 : 6}
                    className="h-12 rounded-xl border-gray-200 text-center text-lg tracking-[0.4em] font-mono"
                  />
                  {error && <p className="text-xs text-red-500 text-center">{error}</p>}
                  <Button
                    type="submit"
                    data-testid="2fa-verify-btn"
                    disabled={loading || (useBackupCode ? twoFACode.length < 8 : twoFACode.length !== 6)}
                    className="w-full h-11 rounded-xl font-semibold text-white text-sm"
                    style={{ background: G }}
                  >
                    {loading ? 'Verifying...' : 'Verify & Sign In'}
                  </Button>
                  {twoFAMethod === 'totp' && (
                    <button
                      type="button"
                      onClick={() => { setUseBackupCode(v => !v); setTwoFACode(''); setError(''); }}
                      className="w-full text-xs text-gray-500 hover:text-gray-700 text-center underline-offset-2 hover:underline"
                      data-testid="2fa-toggle-backup"
                    >
                      {useBackupCode ? '← Use authenticator code instead' : "Lost your phone? Use a backup code"}
                    </button>
                  )}
                  <button type="button" onClick={() => { setMode('login'); setError(''); setPreToken(''); setUseBackupCode(false); }} className="w-full text-xs text-gray-400 hover:text-gray-600 text-center">Back to Sign In</button>
                </form>
              )}

              {/* Forgot Password */}
              {mode === 'forgot' && (
                <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="forgot-form">
                  <div className="text-center mb-2">
                    <h3 className="text-base font-bold text-gray-900">Reset Password</h3>
                    <p className="text-xs text-gray-400 mt-1">Enter your email to receive a reset code</p>
                  </div>
                  <Input data-testid="forgot-email" placeholder="Your registered email" type="email" value={form.email} onChange={e => f('email', e.target.value)} required className="h-11 rounded-xl border-gray-200 text-sm" />
                  {error && <p className="text-xs text-red-500">{error}</p>}
                  {success && <p className="text-xs text-green-600">{success}</p>}
                  <Button type="submit" data-testid="forgot-submit" disabled={loading} className="w-full h-11 rounded-xl font-semibold text-white text-sm" style={{ background: G }}>
                    {loading ? 'Sending...' : 'Send Reset Code'}
                  </Button>
                  <button type="button" onClick={() => { setMode('login'); setError(''); setSuccess(''); }} className="w-full text-xs text-gray-400 hover:text-gray-600 text-center">Back to Sign In</button>
                </form>
              )}

              {/* Reset Confirm */}
              {mode === 'reset-confirm' && (
                <form onSubmit={handleResetConfirm} className="space-y-4" data-testid="reset-confirm-form">
                  <div className="text-center mb-2">
                    <h3 className="text-base font-bold text-gray-900">Enter Reset Code</h3>
                    <p className="text-xs text-gray-400 mt-1">Check your email for the 6-digit code</p>
                    {resetDevCode && <p className="text-[10px] text-orange-500 mt-1">(Dev mode: {resetDevCode})</p>}
                  </div>
                  <Input data-testid="reset-code" placeholder="6-digit reset code" value={resetCode} onChange={e => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))} required maxLength={6} className="h-11 rounded-xl border-gray-200 text-sm text-center tracking-[0.3em] font-mono" />
                  <Input data-testid="reset-new-password" type="password" placeholder="New password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required className="h-11 rounded-xl border-gray-200 text-sm" />
                  {error && <p className="text-xs text-red-500">{error}</p>}
                  {success && <p className="text-xs text-green-600">{success}</p>}
                  <Button type="submit" data-testid="reset-confirm-submit" disabled={loading} className="w-full h-11 rounded-xl font-semibold text-white text-sm" style={{ background: G }}>
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                  <button type="button" onClick={() => { setMode('login'); setError(''); setSuccess(''); }} className="w-full text-xs text-gray-400 hover:text-gray-600 text-center">Back to Sign In</button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
